// Polyfill Node.js Buffer for browser — required by Privy SDK for EIP-712 signing
import { Buffer } from 'buffer';
if (typeof globalThis.Buffer === 'undefined') {
  globalThis.Buffer = Buffer;
}

/**
 * OwnYou Consumer Application Entry Point
 *
 * Sprint 13: Added PrivyProvider for real wallet authentication.
 *
 * Provider Order (dependencies flow downward):
 * 1. QueryClient - React Query for data fetching
 * 2. Router - Navigation
 * 3. Theme - UI theming
 * 4. Privy - Wallet authentication provider (NEW Sprint 13)
 * 5. Auth - OwnYou auth context (wraps Privy)
 * 6. Store - LangGraph memory store
 * 7. Resilience - Circuit breakers and LLM fallback (Sprint 5)
 * 8. Memory - Memory tools and reflection (Sprint 4)
 * 9. Ikigai - Well-being inference (Sprint 6)
 * 10. DataSource - Data connectors (Sprint 8)
 * 11. Trigger - Agent coordination (Sprint 5)
 * 12. Sync - Cross-device sync (Sprint 10)
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
// Initialize persistent logging FIRST - captures all console output to file
import { initLogger } from './utils/logger';
initLogger().catch(console.error);
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PrivyProvider } from '@privy-io/react-auth';
import { ThemeProvider } from '@ownyou/ui-design-system';
import { App } from './App';
import { RootErrorBoundary } from './components/RootErrorBoundary';
// Core contexts
import { AuthProvider } from './contexts/AuthContext';
import { StoreProvider } from './contexts/StoreContext';
import { AuditProvider } from './contexts/AuditContext';
import { SyncProvider } from './contexts/SyncContext';
// Sprint 11a: New integration contexts
import { ResilienceProvider } from './contexts/ResilienceContext';
import { MemoryProvider } from './contexts/MemoryContext';
import { IkigaiProvider } from './contexts/IkigaiContext';
import { DataSourceProvider } from './contexts/DataSourceContext';
import { TriggerProvider } from './contexts/TriggerContext';
// Slice 1 AT-01c + AT-13: Attribution surface (install-consent gate, earnings, delivery log)
import { AttributionProvider } from './contexts/AttributionContext';
// Sprint 11 UX Overhaul Phase 4: Toast notifications
import { ToastProvider } from './contexts/ToastContext';
import './styles/index.css';
// Sprint 15: Log environment status on startup (API keys, feature availability)
import { logEnvironmentStatus } from './config/env-validator';
import { loadPublicRuntimeConfig } from './config/runtime-public-config';
import { emitMissionSpineBaseline } from './lib/mission-spine/instrumentation';
logEnvironmentStatus();
emitMissionSpineBaseline();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Provider composition following dependency order:
 *
 * QueryClient → Router → Theme → Privy → Auth → Store → Resilience → Memory →
 * Ikigai → DataSource → Trigger → Sync → App
 *
 * Why this order:
 * - Privy manages wallet connection lifecycle
 * - Auth wraps Privy and derives encryption keys
 * - Store needs Auth (wallet address for namespace)
 * - Resilience is standalone (no dependencies)
 * - Memory needs Store
 * - Ikigai needs Store and Memory
 * - DataSource needs Store and Resilience
 * - Trigger needs Store, Memory, and access to agents
 * - Sync needs Store
 */
async function bootstrap() {
  const { privyAppId = '' } = await loadPublicRuntimeConfig();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <ToastProvider>
              <PrivyProvider
                appId={privyAppId}
                config={{
                  loginMethods: ['email', 'google', 'apple', 'wallet'],
                  appearance: {
                    theme: 'light',
                    accentColor: '#4F46E5',
                    logo: '/ownyou-logo.svg',
                  },
                  embeddedWallets: {
                    ethereum: {
                      createOnLogin: 'users-without-wallets',
                    },
                  },
                }}
              >
                <AuthProvider>
                  <StoreProvider>
                    <AuditProvider>
                      <ResilienceProvider>
                        <MemoryProvider>
                          <IkigaiProvider>
                            <DataSourceProvider>
                              <TriggerProvider>
                                <SyncProvider>
                                  <AttributionProvider>
                                    <RootErrorBoundary>
                                      <App />
                                    </RootErrorBoundary>
                                  </AttributionProvider>
                                </SyncProvider>
                              </TriggerProvider>
                            </DataSourceProvider>
                          </IkigaiProvider>
                        </MemoryProvider>
                      </ResilienceProvider>
                    </AuditProvider>
                  </StoreProvider>
                </AuthProvider>
              </PrivyProvider>
            </ToastProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
